import React, { useEffect, useState } from 'react';
import { Typography, Box, Theme, TextField } from '@mui/material';
import { createStyles, withStyles, WithStyles } from '@mui/styles';
import { observer, inject } from 'mobx-react';

import StoreModel from 'models/Index';
import {
    Services,
    history,
    RoutePaths,
    countryToCurrencySignMap,
} from 'helpers';
import { NextButton, PrevButton } from 'components/wizard';
import { Form, FormField } from 'models/form';
import validators from 'validators/Index';
import Header from 'components/admin/Header';
import {
    CashCollectionHelpModal,
    CassetteWarningModal,
} from 'containers/modals';

interface CollectionProps extends WithStyles<typeof styles> {}
interface CollectionInject extends CollectionProps {
    Store: StoreModel;
}

const styles = (theme: Theme) =>
    createStyles({
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            height: '170px',
        },
        headerLine: {
            height: '6px',
            background: '#e0e0e0',
        },
        main: {
            margin: '0px 60px',
        },
        title: {
            marginTop: '66px',
        },
        subTitle: {
            marginTop: '36px',
        },
        banknotesTitle: {
            marginTop: '40px',
        },
        banknotesBox: {
            padding: '40px 0px 13px 0px',
            display: 'flex',
            flexDirection: 'column',
            gap: '17px',
        },
        banknotesItem: {
            display: 'flex',
        },
        denominationValue: {
            width: '50px',
            textAlign: 'end',
        },
        signMult: {
            marginLeft: '15px',
            fontWeight: '500',
        },
        textFieldBox: {
            marginLeft: '15px',
            width: '80px',
        },
        signEq: {
            marginLeft: '25px',
        },
        denominationTotal: {
            marginLeft: '40px',
        },
        summaryBox: {
            marginTop: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
        },
        summaryBoxItem: {
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '75px',
        },
        totalLabel: {
            width: '150px',
        },
        totalResult: {
            width: '110px',
            textAlign: 'right',
        },
        buttonBox: {
            display: 'flex',
            left: 60,
            right: 60,
            position: 'fixed',
            bottom: '105px',
            justifyContent: 'space-between',
        },
        underline: {
            marginTop: '10px',
            borderTop: '1px solid #343434',
        },
    });

const Collection = ({ classes, Store }: CollectionInject) => {
    const [isOpenHelpModal, setIsOpenHelpModal] = useState(false);
    const [isOpenCassetteWarningModal, setIsOpenCassetteWarningModal] =
        useState(false);

    const denominationList = Store.configuration.denominationList;
    const currencySign = countryToCurrencySignMap[denominationList[0].country];

    const [totalCount, setTotalCount] = useState(
        Store.collection.userCashMetrics
            ? Store.collection.userCashMetrics.totalCount
            : 0
    );
    const [totalAmount, setTotalAmount] = useState(
        Store.collection.userCashMetrics
            ? Store.collection.userCashMetrics.totalAmount
            : 0
    );

    const [form] = useState(
        Store.collection.userCashMetrics
            ? new Form().init(
                  Store.collection.userCashMetrics.denominations.map(
                      (denomination): FormField => {
                          return new FormField(
                              denomination.denomination,
                              denomination.count,
                              [validators.required()]
                          );
                      }
                  )
              )
            : new Form().init(
                  denominationList.map((denomination): FormField => {
                      return new FormField(denomination.value.toString(), 0, [
                          validators.required(),
                      ]);
                  })
              )
    );

    useEffect(() => {
        if (
            !Store.peripheryStatus.isCassetteInserted &&
            !Store.collection.userCashMetrics
        ) {
            setIsOpenHelpModal(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const formFields = form.fieldsObj;

    const calculate = () => {
        let count = 0;
        let amount = 0;
        form.fields.forEach((field) => {
            count += field.value;
            amount += field.value * Number(field.name);
        });

        setTotalCount(count);
        setTotalAmount(amount);
    };

    return (
        <>
            <Box className={classes.header}>
                <Header />
            </Box>
            <div className={classes.headerLine}></div>

            <Box className={classes.main}>
                <Typography
                    color='primary'
                    variant='h2'
                    align='center'
                    className={classes.title}
                >
                    Cash Collection
                </Typography>

                <Typography
                    variant='h4'
                    align='center'
                    className={classes.subTitle}
                >
                    Please count all of the cash in the kiosk by denomination.
                </Typography>
                <Typography variant='h4' align='center'>
                    Enter the number for each banknote below.
                </Typography>

                <Typography
                    variant='h4'
                    align='left'
                    className={classes.banknotesTitle}
                >
                    Banknotes
                </Typography>

                <hr className={classes.underline} />

                <Box className={classes.banknotesBox}>
                    {denominationList.map((denomination, index) => {
                        return (
                            <Box
                                key={denomination.index}
                                className={classes.banknotesItem}
                            >
                                <Typography
                                    className={classes.denominationValue}
                                    variant='h5'
                                >{`${currencySign}${denomination.value}`}</Typography>
                                <Typography
                                    variant='h5'
                                    className={classes.signMult}
                                >
                                    &times;
                                </Typography>
                                <Box className={classes.textFieldBox}>
                                    <TextField
                                        value={
                                            formFields[denomination.value].value
                                        }
                                        inputRef={
                                            formFields[denomination.value].ref
                                        }
                                        error={
                                            formFields[denomination.value]
                                                .hasError
                                        }
                                        tabIndex={index}
                                        variant='standard'
                                        onChange={(e) => {
                                            const value = +e.target.value;
                                            if (
                                                !isNaN(value) &&
                                                value <= 10000
                                            ) {
                                                formFields[
                                                    denomination.value
                                                ].setValue(value);
                                                calculate();
                                            }
                                        }}
                                    />
                                </Box>
                                <Typography className={classes.signEq}>
                                    =
                                </Typography>
                                <Typography
                                    variant='h5'
                                    className={classes.denominationTotal}
                                >
                                    {`${currencySign}${(
                                        formFields[denomination.value].value *
                                        denomination.value
                                    ).toLocaleString('en-US')}`}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>

                <hr className={classes.underline} />

                <Box className={classes.summaryBox}>
                    <Box className={classes.summaryBoxItem}>
                        <Typography
                            className={classes.totalLabel}
                            variant='h4'
                            align='left'
                        >
                            Total Count
                        </Typography>

                        <Typography
                            className={classes.totalResult}
                            variant='h4'
                            align='left'
                        >
                            {`${totalCount}`}
                        </Typography>
                    </Box>
                    <Box className={classes.summaryBoxItem}>
                        <Typography
                            className={classes.totalLabel}
                            variant='h4'
                            align='left'
                        >
                            Total Value
                        </Typography>

                        <Typography
                            className={classes.totalResult}
                            variant='h4'
                            align='left'
                        >
                            {`${currencySign}${totalAmount.toLocaleString(
                                'en-US'
                            )}`}
                        </Typography>
                    </Box>
                </Box>

                <Box className={classes.buttonBox}>
                    <PrevButton
                        buttonProps={{
                            onClick: () => {
                                if (Store.peripheryStatus.isCassetteInserted) {
                                    Store.collection.cleanData();
                                    history.push({
                                        pathname: RoutePaths.IdleScreen,
                                        search: window.location.search,
                                    });
                                } else {
                                    setIsOpenCassetteWarningModal(true);
                                }
                            },
                        }}
                    />
                    <NextButton
                        title='Next'
                        buttonProps={{
                            disabled: totalCount === 0,
                            onClick: () => {
                                Store.collection.setUserCashMetrics({
                                    totalAmount: totalAmount,
                                    totalCount: totalCount,
                                    currencySign: currencySign,
                                    denominations: form.fields.map((field) => {
                                        return {
                                            denomination: field.name,
                                            count: field.value,
                                            amount: +field.name * field.value,
                                        };
                                    }),
                                });
                                history.push({
                                    pathname: RoutePaths.CashCollectionSummary,
                                    search: window.location.search,
                                });
                            },
                        }}
                    />
                </Box>
            </Box>
            <CashCollectionHelpModal
                isOpen={isOpenHelpModal}
                onClose={() => setIsOpenHelpModal(false)}
            />
            <CassetteWarningModal
                isOpen={isOpenCassetteWarningModal}
                onClose={() => setIsOpenCassetteWarningModal(false)}
            />
        </>
    );
};

export default withStyles(styles)(inject(Services.STORE)(observer(Collection)));
